/**
 * Dev-only mock completed cycle. Drives the post-cycle preview on the
 * home page so designers can see what `CycleCompleteView` looks like
 * without having a real closed cycle in the backend.
 *
 * Enabled with `?dev_force_complete_cycle=true` on a `__DEV__` build.
 * Persisted in sessionStorage for the tab. Same pattern as
 * `devActiveCycleOverride.ts`.
 *
 * The mock describes a completed egg-freezing cycle with 11 eggs
 * frozen out of 14 retrieved. The shape mirrors what `useCycleComplete`
 * normally derives from the cycles + cryo-vault APIs.
 */
import type {
  CryoStorageInfo,
  CycleResult,
  WhatsNextOption,
} from "../../homepage/plan/components/CycleCompleteView";

const STORAGE_KEY = "__moon_dev_force_complete_cycle";

export const DEV_FORCE_COMPLETE_CYCLE_OVERRIDE: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_force_complete_cycle",
  );
  if (fromUrl === "true" || fromUrl === "1") {
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, "1");
    } catch {
      /* sessionStorage may be disabled */
    }
    return true;
  }
  if (fromUrl === "false" || fromUrl === "0") {
    try {
      window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return false;
  }
  try {
    return window.sessionStorage?.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
})();

export const MOCK_COMPLETED_CYCLE_RESULT: CycleResult = {
  cycleType: "Egg Freezing",
  eggsRetrieved: 14,
  matureEggs: 11,
  frozen: 11,
  pregnancyResult: null,
  interpretation:
    "11 mature eggs frozen out of 14 retrieved. A maturity rate around 79% means stimulation, trigger timing, and retrieval landed in the expected range.",
};

export const MOCK_COMPLETED_CRYO_STORAGE: CryoStorageInfo = {
  itemCount: 11,
  itemType: "eggs",
  facilityName: "Louise Fertility Clinic",
  annualCost: "$600/yr",
  nextPaymentDate: "May 24, 2027",
};

export const MOCK_COMPLETED_WHATS_NEXT: WhatsNextOption[] = [
  {
    id: "freeze-more",
    title: "Freeze more eggs",
    description:
      "Many women do 2-3 cycles. Each cycle adds to your total bank. Your Learn and Test steps carry over.",
    icon: "repeat",
    action: { screen: "CycleDetailScreen", label: "Start another cycle" },
  },
  {
    id: "use-eggs",
    title: "Use my eggs for IVF",
    description:
      "When you're ready, your eggs are thawed, fertilized, and transferred.",
    icon: "baby",
    action: { screen: "FindCareScreen", label: "Find a clinic" },
  },
  {
    id: "store",
    title: "Just store for now",
    description:
      "Your eggs are safely stored. We'll remind you about storage payments.",
    icon: "pause",
    action: { screen: "CryoVaultScreen", label: "View cryo vault" },
  },
  {
    id: "support",
    title: "Talk to someone",
    description:
      "Processing the experience is normal. Connect with support resources.",
    icon: "heart",
  },
];
